import type {
  IsBillingEnabledFn,
  ProfilePaymentMethodsViewModelFactory,
  StripeConnectCallbackModalViewModelFactory,
} from '@/features/billing'
import type {
  IsPayoutsEnabledFn,
  ProfilePayoutMethodViewModelFactory,
} from '@/features/payouts'
import type { NavigateFn } from '@/RootStore'
import { type Member } from '@/features/iam/members/Member'
import { ProfileGeneralViewModel } from '@/features/iam/users/ProfileGeneralViewModel'
import { ProfileBillingViewModel } from '@/features/iam/users/ProfileBillingViewModel'
import { task } from 'mobx-task'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type MemberStore } from '@/features/iam/members/MemberStore'
import { type SkillStore } from '@/features/iam/skills/SkillStore'
import { type RoleStore } from '@/features/iam/roles/RoleStore'
import { ProfileNotificationsViewModel } from '@/features/iam/users/ProfileNotificationsViewModel'
import type {
  FetchNotificationPreferencesFn,
  SaveNotificationPreferencesFn,
} from '@/features/notifications'
import { computed } from 'mobx'
import { type ProviderStore } from '@/features/iam/providers/ProviderStore'
import { type UserDataStore } from '@/features/iam/users/UserDataStore'
import { ProDetailsViewModel } from '@/features/iam/providers/ProDetails/ProDetailsViewModel'

export class ProfileViewModel {
  readonly general: ProfileGeneralViewModel
  readonly proDetails: ProDetailsViewModel
  readonly billing: ProfileBillingViewModel
  readonly notifications: ProfileNotificationsViewModel

  constructor(
    public readonly member: Member,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _memberStore: MemberStore,
    private readonly _skillStore: SkillStore,
    private readonly _roleStore: RoleStore,
    private readonly _fetchNotificationPreferences: FetchNotificationPreferencesFn,
    private readonly _saveNotificationPreferences: SaveNotificationPreferencesFn,
    private readonly _isBillingEnabled: IsBillingEnabledFn,
    private readonly _isPayoutsEnabled: IsPayoutsEnabledFn,
    private readonly _createProfilePaymentMethodsViewModel: ProfilePaymentMethodsViewModelFactory,
    private readonly _createProfilePayoutMethodViewModel: ProfilePayoutMethodViewModelFactory,
    private readonly _createStripeConnectCallbackModalViewModel: StripeConnectCallbackModalViewModelFactory,
    private readonly _navigate: NavigateFn,
    private readonly _iamProviderStore: ProviderStore,
    private readonly _userDataStore: UserDataStore,
  ) {
    this.general = new ProfileGeneralViewModel(
      this._newAlert,
      this._createAlertErrorHandler,
      this._createAlertRetryWrapper,
      this.member,
      this._memberStore,
      this._skillStore,
      this._roleStore,
    )

    this.proDetails = new ProDetailsViewModel(
      this._newAlert,
      this._createAlertErrorHandler,
      this._iamProviderStore,
      this.member.user.publicId,
      true,
    )

    this.billing = new ProfileBillingViewModel(
      this.member,
      this._isPayoutsEnabled,
      this._createProfilePaymentMethodsViewModel,
      this._createProfilePayoutMethodViewModel,
      this._createStripeConnectCallbackModalViewModel,
      this._navigate,
    )

    this.notifications = new ProfileNotificationsViewModel(
      this.member,
      this._newAlert,
      this._fetchNotificationPreferences,
      this._saveNotificationPreferences,
    )
  }

  @computed
  get isPro() {
    return Boolean(
      this._userDataStore.getByPublicId(this.member.user.publicId)
        ?.userDataType === 'Pro',
    )
  }

  @computed
  get canViewAndEditOwnSkills() {
    return this.member.permissions.updateOwnSkills
  }

  @computed
  get canViewAndEditOwnRoles() {
    return this.member.permissions.adminUserRolesModify
  }

  @computed
  get shouldShowBilling() {
    return this._isBillingEnabled()
  }

  /**
   * Uploads a new avatar.
   */
  uploadNewAvatar = task.resolved(async (file: File) => {
    // 10mb, give or take.
    if (file.size > 1024 * 1024 * 10) {
      this._newAlert()
        .withState('error')
        .saying('File is too big', 'Please keep it at 10mb or less.')
        .autoDismiss()
        .show()
      return
    }
    try {
      await this._memberStore
        .changeAvatar(this.member, file)
        .catch(this._createAlertErrorHandler('Changing avatar failed'))
    } catch {
      return
    }
    this._newAlert()
      .withState('success')
      .saying('Success!', 'Your avatar has been changed.')
      .autoDismiss()
      .show()
  })
}
